import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  ACCESS_CODES,
  api,
  ApiError,
  get,
  hasSession,
  lastRefreshWasOffline,
  onAccessProblem,
  onSessionChange,
  refreshSession,
  setSession,
} from './api.js';
import { clearQueue } from './offline-queue.js';

export type DataScope = 'OWN' | 'TEAM' | 'PROJECT' | 'ALL';

export interface Me {
  user: { id: string; email: string; twoFactorEnabled: boolean };
  member: {
    id: string;
    displayName: string;
    phoneE164: string | null;
    teamId: string | null;
    managerId: string | null;
    /** First-run tours finished or skipped (D-142). */
    toursSeen?: string[];
  };
  role: { id: string; key: string; name: string };
  permissions: Record<string, DataScope>;
  /** `timezone`: the organisation's calendar, in which the app shows days and times. */
  organisation: { slug: string; brandName: string; timezone?: string };
  /** `payments`, `attendance`: parts the organisation has switched on (D-122); on when absent. */
  features: { multiCompany: boolean; payments?: boolean; attendance?: boolean };
  /** Set-up wizard progress; null for members who do not run it. */
  setup: { completed: boolean; done: number; total: number; next: string | null } | null;
}

interface AuthState {
  /**
   * mfa_enrolment: the role requires 2-step verification the member has not set up yet;
   * network_blocked: the role's allowed networks do not include this one (both decided by the server);
   * offline: the app opened with no connection, so the session could not be restored yet (D-140).
   */
  status: 'loading' | 'signed_out' | 'signed_in' | 'mfa_enrolment' | 'network_blocked' | 'offline';
  me: Me | undefined;
  can: (permission: string) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [booted, setBooted] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    // Restore the session from the refresh cookie on first load; with no connection, try again when it
    // comes back (the cached app shows the offline screen meanwhile).
    const restore = () =>
      refreshSession()
        .then(() => setOffline(lastRefreshWasOffline()))
        .finally(() => setBooted(true));
    void restore();
    const onOnline = () => {
      if (lastRefreshWasOffline()) void restore();
    };
    window.addEventListener('online', onOnline);
    const offSession = onSessionChange((s) => {
      setSignedIn(s);
      if (!s) qc.clear();
    });
    // A request refused for the whole session (role now needs 2FA, network no longer allowed):
    // re-ask who we are, which moves the app to the matching screen.
    const offAccess = onAccessProblem(() => void qc.invalidateQueries({ queryKey: ['me'] }));
    return () => {
      window.removeEventListener('online', onOnline);
      offSession();
      offAccess();
    };
  }, [qc]);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => get<Me>('/auth/me'),
    enabled: signedIn,
    staleTime: 60_000,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
  });
  const accessCode =
    me.error instanceof ApiError && me.error.problem.code && ACCESS_CODES.has(me.error.problem.code)
      ? me.error.problem.code
      : undefined;

  const value = useMemo<AuthState>(
    () => ({
      status:
        !booted || (signedIn && me.isPending)
          ? 'loading'
          : !signedIn && offline
            ? 'offline'
            : signedIn && accessCode === 'mfa_enrolment_required'
              ? 'mfa_enrolment'
              : signedIn && accessCode === 'ip_not_allowed'
                ? 'network_blocked'
                : signedIn && me.data
                  ? 'signed_in'
                  : 'signed_out',
      me: me.data,
      can: (p) => !!me.data?.permissions[p],
      signOut: async () => {
        try {
          await api('POST', '/auth/logout', {}, { 'x-tatra-csrf': '1' }).catch(() => undefined);
        } finally {
          // Notes waiting to be sent go with the session (a shared phone must not keep them).
          clearQueue();
          setSession(null);
        }
      },
    }),
    [booted, signedIn, offline, me.isPending, me.data, accessCode],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

export { hasSession };

/** Whether the organisation handles money in the CRM (D-122). */
export function useMoney(): boolean {
  return useAuth().me?.features.payments !== false;
}
